import { DefaultResponse, PageResponse } from '@/common/types/common';
import {
  BillingProductItem,
  BillingServiceType,
  BillingStatusResponse,
  ChangePlanRequest,
  CreateConsultationInquiryRequest,
  CreateRefundRequest,
  CreateSubscriptionRequest,
  CustomerKeyResponse,
  PaymentRecordInvoice,
  PaymentRecordItem,
  RefundRequestItem,
  RegisterBillingKeyRequest,
  ServiceEntitlementDto,
} from '@/types/billing';
import { apiService } from './apiService';
import { billingEndpoints } from '@/api/apiEndpoint';

export interface ProductChangeResponse {
  success: boolean;
  insertedId?: number;
  eventType: 'NONE' | 'CHARGE' | 'REFUND';
  amount?: number;
  message: string;
}

export const billingService = {
  async getPlans(
    serviceType: BillingServiceType,
    productType: string = 'PLAN',
  ): Promise<BillingProductItem[]> {
    return apiService.get<BillingProductItem[]>(
      billingEndpoints.getPlans(serviceType, productType),
    );
  },

  async getCustomerKey(companyId: number): Promise<CustomerKeyResponse> {
    return apiService.get<CustomerKeyResponse>(
      billingEndpoints.getCustomerKey(companyId),
    );
  },

  async registerBillingKey(data: RegisterBillingKeyRequest): Promise<DefaultResponse> {
    return apiService.post<DefaultResponse>(
      billingEndpoints.registerBillingKey(),
      data,
    );
  },

  async deleteBillingKey(companyId: number): Promise<DefaultResponse> {
    return apiService.delete<DefaultResponse>(
      billingEndpoints.deleteBillingKey(companyId),
    );
  },

  async getBillingStatus(companyId: number): Promise<BillingStatusResponse> {
    return apiService.get<BillingStatusResponse>(
      billingEndpoints.getBillingStatus(companyId),
    );
  },

  async getEntitlement(companyId: number): Promise<ServiceEntitlementDto> {
    return apiService.get<ServiceEntitlementDto>(
      billingEndpoints.getEntitlement(companyId),
    );
  },

  async createSubscription(data: CreateSubscriptionRequest): Promise<ProductChangeResponse> {
    return apiService.post<ProductChangeResponse>(
      billingEndpoints.createSubscription(),
      data,
    );
  },

  async createFreeSubscription(data: CreateSubscriptionRequest): Promise<DefaultResponse> {
    return apiService.post<DefaultResponse>(
      billingEndpoints.createFreeSubscription(),
      data,
    );
  },

  async cancelSubscription(
    companyId: number,
    subscriptionId: number,
  ): Promise<DefaultResponse> {
    return apiService.patch<DefaultResponse>(
      billingEndpoints.cancelSubscription(companyId, subscriptionId),
    );
  },

  async revertCancelSubscription(
    companyId: number,
    subscriptionId: number,
  ): Promise<DefaultResponse> {
    return apiService.patch<DefaultResponse>(
      billingEndpoints.revertCancelSubscription(companyId, subscriptionId),
    );
  },

  async getPayments(
    companyId: number,
    page: number = 1,
    limit: number = 10,
  ): Promise<PageResponse<PaymentRecordItem>> {
    return apiService.get<PageResponse<PaymentRecordItem>>(
      billingEndpoints.getPayments(companyId),
      { page, limit },
    );
  },

  async getInvoice(invoiceId: number): Promise<PaymentRecordInvoice> {
    return apiService.get<PaymentRecordInvoice>(
      billingEndpoints.getInvoice(invoiceId),
    );
  },

  async createRefundRequest(
    companyId: number,
    data: CreateRefundRequest,
  ): Promise<DefaultResponse> {
    return apiService.post<DefaultResponse>(
      billingEndpoints.createRefundRequest(companyId),
      data,
    );
  },

  async getRefundRequests(companyId: number): Promise<RefundRequestItem[]> {
    return apiService.get<RefundRequestItem[]>(
      billingEndpoints.getRefundRequests(companyId),
    );
  },

  async createConsultationInquiry(
    data: CreateConsultationInquiryRequest,
  ): Promise<DefaultResponse> {
    return apiService.post<DefaultResponse>(
      billingEndpoints.createConsultationInquiry(),
      data,
    );
  },

  async getChangeablePlans(
    companyId: number,
    subscriptionId: number,
  ): Promise<BillingProductItem[]> {
    return apiService.get<BillingProductItem[]>(
      billingEndpoints.getChangeablePlans(companyId, subscriptionId),
    );
  },

  async changePlan(
    companyId: number,
    subscriptionId: number,
    data: ChangePlanRequest,
  ): Promise<ProductChangeResponse> {
    return apiService.post<ProductChangeResponse>(
      billingEndpoints.changePlan(companyId, subscriptionId),
      data,
    );
  },
};
